import {
  FaUserCircle,
  FaRegNewspaper,
  FaSignOutAlt,
  FaChevronDown
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Dropdown, Menu } from "antd";
import { logout } from "../API/auth";
import { checkUserPermissions, USER_GROUPS } from "../utils/permissions";
import PortalUemHeader, { mainNavTriggerClass } from "../Components/Layout/PortalUemHeader";
import { ExternalLink } from "lucide-react";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { isGraduated, isEmployer } = checkUserPermissions();

  const landingPageItems = [
    { name: "Home", href: "/" },
    {
      name: "Sobre Nós",
      href: "/sobre",
      submenu: [
        { name: "O que é Alumni", href: "/o-que-e-alumni" },
        { name: "Objectivos da Comunidade", href: "/objectivos" },
        { name: "Dia do Alumni", href: "/dia-do-alumni" },
        { name: "Fundo Alumni", href: "/fundo-alumni" }
      ]
    },
    { name: "Embaixadores", href: "/embaixadores" },
    { name: "Empregabilidade", href: "/" },
    { name: "Núcleos Alumni", href: "/nucleos" },
    { name: "Contactos", href: "/contactos" },
  ];

  const getAllowedMainItems = () => {
    const allItems = [
      { name: "Feed", href: "/feed", allowedGroups: [USER_GROUPS.GRADUATED, USER_GROUPS.EMPLOYER] },
      { name: "Eventos", href: "/eventos", allowedGroups: [USER_GROUPS.GRADUATED, USER_GROUPS.EMPLOYER] },
      { name: "Publicar", href: "/publicar", allowedGroups: [USER_GROUPS.GRADUATED] },
      { name: "Vagas", href: "/vagas", allowedGroups: [USER_GROUPS.GRADUATED, USER_GROUPS.EMPLOYER] },
      { name: "Parceiros", href: "/parceiros", allowedGroups: [USER_GROUPS.EMPLOYER] },
      { name: "Empregabilidade", href: "/empregabilidade", allowedGroups: [USER_GROUPS.GRADUATED, USER_GROUPS.EMPLOYER] },
      { name: "Iniciativa Padrinho", href: "/padrinho", allowedGroups: [USER_GROUPS.GRADUATED] },
      { name: "Colaboração", href: "/pedido-colaboracao", allowedGroups: [USER_GROUPS.GRADUATED] },
      { name: "Doações", href: "/doar", allowedGroups: [USER_GROUPS.GRADUATED, USER_GROUPS.EMPLOYER] },
      
    ];

    return allItems.filter((item) => {
      return (
        (item.allowedGroups.includes(USER_GROUPS.GRADUATED) && isGraduated) ||
        (item.allowedGroups.includes(USER_GROUPS.EMPLOYER) && isEmployer)
      );
    });
  };

  const mainItems = getAllowedMainItems();

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isSobreActive = (item) =>
    location.pathname === item.href ||
    (item.submenu && item.submenu.some((sub) => sub.href === location.pathname));

  const userDropdownPanel = (compact) => (
    <div className={`overflow-hidden z-50 rounded-xl border border-gray-100 bg-white shadow-lg ${compact ? "w-48 py-1" : "w-56 py-2"}`}>
      <button
        type="button"
        onClick={() => {
          navigate("/perfil");
          setIsMenuOpen(false);
        }}
        className={`flex w-full items-center gap-3 px-4 text-gray-600 transition-colors hover:bg-gray-50 hover:text-uem-primary ${compact ? "py-2 text-xs" : "py-2.5 text-sm"}`}
      >
        <FaUserCircle size={compact ? 14 : 16} className="text-gray-400" />
        <span>Meu Perfil</span>
      </button>
      {isGraduated && (
        <button
          type="button"
          onClick={() => {
            navigate("/minhas-publicacoes");
            setIsMenuOpen(false);
          }}
          className={`flex w-full items-center gap-3 px-4 text-gray-600 transition-colors hover:bg-gray-50 hover:text-uem-primary ${compact ? "py-2 text-xs" : "py-2.5 text-sm"}`}
        >
          <FaRegNewspaper size={compact ? 14 : 16} className="text-gray-400" />
          <span>Minhas publicações</span>
        </button>
      )}
      <div className="my-1 border-t border-gray-100" />
      <button
        type="button"
        onClick={() => {
          logout();
          setIsMenuOpen(false);
        }}
        className={`flex w-full items-center gap-3 px-4 text-red-500 transition-colors hover:bg-red-50 hover:text-red-600 ${compact ? "py-2 text-xs" : "py-2.5 text-sm"}`}
      >
        <FaSignOutAlt size={compact ? 14 : 16} className="text-red-400" />
        <span>Sair</span>
      </button>
    </div>
  );

  const rightSlot =
    location.pathname === "/" ? (
      <button
        type="button"
        onClick={() => navigate("/feed")}
        className="rounded-lg bg-uem-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-uem-primaryHover"
      >
        Entrar
      </button>
    ) : (
      <div ref={menuRef} className="relative flex items-center">
        <button
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="flex items-center justify-center transition hover:opacity-90"
          aria-expanded={isMenuOpen}
          aria-haspopup="true"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-full border border-uem-primary/35 bg-white text-uem-primary md:h-10 md:w-10">
            <FaUserCircle size={22} />
          </div>
        </button>
        {isMenuOpen && (
          <div className="absolute right-0 top-full z-[60] mt-2 md:hidden">
            {userDropdownPanel(true)}
          </div>
        )}
        {isMenuOpen && (
          <div className="absolute right-0 top-full z-[60] mt-2 hidden md:block">
            {userDropdownPanel(false)}
          </div>
        )}
      </div>
    );

  const landingMainNav = (
    <>
      {landingPageItems.map((item) =>
        item.submenu ? (
          <Dropdown
            key={item.href}
            overlay={
              <Menu className="rounded-xl border border-gray-100 shadow-lg">
                {item.submenu.map((subItem) => (
                  <Menu.Item key={subItem.href}>
                    <button
                      type="button"
                      onClick={() => navigate(subItem.href)}
                      className="w-full text-left text-gray-600 hover:text-uem-primary"
                    >
                      {subItem.name}
                    </button>
                  </Menu.Item>
                ))}
              </Menu>
            }
            trigger={['hover']}
          >
            <button type="button" className={mainNavTriggerClass(isSobreActive(item))}>
              <span>{item.name}</span>
              <FaChevronDown size={12} className="opacity-90" />
            </button>
          </Dropdown>
        ) : (
          <button
            key={item.href}
            type="button"
            onClick={() => navigate(item.href)}
            className={mainNavTriggerClass(location.pathname === item.href)}
          >
            {item.name}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => navigate("/feed")}
        className={`${mainNavTriggerClass(false)} bg-white/10 hover:bg-white/20`}
      >
        Entrar
      </button>
    </>
  );

  const platformMainNav = (
    <>
      {mainItems.map((item) => (
        <button
          key={item.href}
          type="button"
          onClick={() => navigate(item.href)}
          className={mainNavTriggerClass(location.pathname === item.href)}
        >
          {item.name}
        </button>
      ))}
    </>
  );

  let mainNav = null;
  if (location.pathname === "/" && landingPageItems.length) {
    mainNav = landingMainNav;
  } else if (location.pathname !== "/" && mainItems.length > 0) {
    mainNav = platformMainNav;
  }

  return (
    <PortalUemHeader
      position="fixed"
      onBrandClick={() => navigate("/")}
      rightSlot={rightSlot}
      utilityRight={
        <a
          href="https://www.uem.mz"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-semibold uppercase tracking-wide hover:underline"
        >
          Universidade Eduardo Mondlane
          <ExternalLink size={14} className="opacity-90 text-white" />
        </a>
      }
      mainNav={mainNav}
    />
  );
}
